import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  MasterAdminUserMgmtService,
  KeycloakUserDTO,
  CreateUserRequestDTO,
  RealmRoleDTO,
  GroupDTO,
  SessionDTO,
  LoginEventFilter,
  LoginEventRow,
  UserListFilter,
} from '../tokens/master-admin-user-mgmt.token';
import { API_PATHS } from '../config/api-paths';

/**
 * REST-backed implementation of `MasterAdminUserMgmtService`.
 *
 * All calls go to `/services/embedded/api/master-admin/...`
 * and require ROLE_MASTER_ADMIN (enforced server-side; the portal
 * roleGuard gates the route).
 *
 * Rule 09 — error surfacing is the component's responsibility via
 * `extractBackendErrorMessage`. This service only throws; it does not
 * translate or swallow errors.
 *
 * Rule 10 — REST-only. No mock fallback. Playwright specs use
 * `page.route()`.
 */
@Injectable()
export class MasterAdminUserMgmtRestService implements MasterAdminUserMgmtService {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return `${API_PATHS.EMBEDDED_API_BASE}/master-admin`;
  }

  // ── Users ─────────────────────────────────────────────────────────────────

  /**
   * EM-1654 — server-side paginated + searchable user listing.
   *
   * The endpoint used to ignore every parameter and return the first 100 accounts, so the Users tab
   * could not reach anything past them. It now pages, searches (`search`) and filters by realm role
   * (`role`), and reports the true match count in `X-Total-Count`.
   */
  listUsers(
    filter: UserListFilter,
    page: number,
    size: number,
  ): Observable<{ items: KeycloakUserDTO[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    if (filter.search?.trim()) {
      params = params.set('search', filter.search.trim());
    }
    if (filter.role?.trim()) {
      params = params.set('role', filter.role.trim());
    }

    return this.http.get<KeycloakUserDTO[]>(`${this.base}/users`, {
      params,
      observe: 'response',
    }).pipe(
      map(resp => {
        const totalHeader = resp.headers.get('X-Total-Count');
        const items = resp.body ?? [];
        return {
          items,
          total: totalHeader ? parseInt(totalHeader, 10) : items.length,
        };
      }),
    );
  }

  createUser(dto: CreateUserRequestDTO): Observable<KeycloakUserDTO> {
    return this.http.post<KeycloakUserDTO>(`${this.base}/users`, dto);
  }

  setEnabled(userId: string, enabled: boolean): Observable<void> {
    return this.http.put<void>(`${this.base}/users/${userId}/enabled`, { enabled });
  }

  assignRole(userId: string, role: string): Observable<void> {
    return this.http.post<void>(`${this.base}/users/${userId}/roles`, { role });
  }

  removeRole(userId: string, role: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/users/${userId}/roles/${role}`);
  }

  setTempPassword(userId: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.base}/users/${userId}/reset-password`, { newPassword });
  }

  // ── Roles ─────────────────────────────────────────────────────────────────

  listRoles(withMembers = true): Observable<RealmRoleDTO[]> {
    const params = new HttpParams().set('withMembers', String(withMembers));
    return this.http.get<RealmRoleDTO[]>(`${this.base}/roles`, { params });
  }

  // ── Groups ────────────────────────────────────────────────────────────────

  listGroups(): Observable<GroupDTO[]> {
    return this.http.get<GroupDTO[]>(`${this.base}/groups`);
  }

  addGroupMember(groupId: string, userId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/groups/${groupId}/members/${userId}`, null);
  }

  removeGroupMember(groupId: string, userId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/groups/${groupId}/members/${userId}`);
  }

  // ── Sessions ──────────────────────────────────────────────────────────────

  listSessions(): Observable<SessionDTO[]> {
    return this.http.get<SessionDTO[]>(`${this.base}/sessions`);
  }

  terminateSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/sessions/${sessionId}`);
  }

  // ── Login Events ─────────────────────────────────────────────────────────

  listLoginEvents(
    filter: LoginEventFilter,
    page: number,
    size: number,
  ): Observable<{ items: LoginEventRow[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    if (filter.userLogin?.trim()) {
      params = params.set('userLogin', filter.userLogin.trim());
    }
    if (filter.outcome?.trim()) {
      params = params.set('outcome', filter.outcome.trim());
    }
    if (filter.from?.trim()) {
      // datetime-local format: "2026-06-01T12:00" → ISO: "2026-06-01T12:00:00Z"
      params = params.set('from', new Date(filter.from.trim()).toISOString());
    }
    if (filter.to?.trim()) {
      params = params.set('to', new Date(filter.to.trim()).toISOString());
    }

    return this.http.get<LoginEventRow[]>(`${this.base}/login-events`, {
      params,
      observe: 'response',
    }).pipe(
      map(resp => {
        const totalHeader = resp.headers.get('X-Total-Count');
        return {
          items: resp.body ?? [],
          total: totalHeader ? parseInt(totalHeader, 10) : 0,
        };
      }),
    );
  }
}
